import {
  BaseSyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useQuery } from '@apollo/client'
import { IconPlus } from '@tabler/icons-react'
import { uniqBy } from 'lodash'
import styled from 'styled-components'

import { useDebounce } from '../../hooks/useDebounce'
import { Button } from '../shared-components/BUI/Button'
import { Select, SelectProps } from '../shared-components/BUI/Select'
import { Text } from '../shared-components/BUI/Text'
import { Flexbox } from '../shared-components/Flexbox'

import { GET_WORKERS_NAME } from '@/modules/workers/graphQL'
import NewWorkerModal from '@/modules/workers/NewWorkerModal'
import { getWorkerName } from '@/utils/index'

import {
  CreateWorkerWithUserUiMutation,
  WorkersNameUiQuery,
  WorkersNameUiQueryVariables,
} from 'types/api.graphql'

const AddButton = styled(Button)`
  margin: 1rem 0 1rem 0.5rem;
`

const DEBOUNCE_TIMEOUT = 400
const INITIAL_LIMIT = 20
const SUBSEQUENT_LIMIT = 20

const WorkerDropDown = ({ menu, openWorkerModal }) => (
  <>
    {menu}
    <AddButton
      size="small"
      onClick={openWorkerModal}
      icon={<IconPlus size={16} />}
    >
      New worker
    </AddButton>
  </>
)

export type WorkerInputValue = SelectProps['value']

type Props = {
  value?: WorkerInputValue
  onChange?: (value: SelectProps['value']) => void
  multiple?: boolean
  projectId?: string
  hideSubbieOrigin?: boolean
}

export const WorkerInput: React.FC<Props> = ({
  value,
  onChange,
  multiple,
  projectId,
  hideSubbieOrigin,
}) => {
  const offset = useRef(0)
  const initialValue = useRef(value) // store the initial value to avoid re-fetching on every render

  const [totalCount, setTotalCount] = useState<number>()
  const [searchString, setSearchString] = useState<string>()
  const [workerModalVisible, setWorkerModalVisible] = useState(false)
  const [currentlyLoadingMore, setCurrentlyLoadingMore] = useState(false)

  const [addedWorkers, setAddedWorkers] = useState<
    WorkersNameUiQuery['workers']
  >([])

  const debouncedSearchString = useDebounce(searchString, DEBOUNCE_TIMEOUT)

  const { data: initialWorkersData } = useQuery<
    WorkersNameUiQuery,
    WorkersNameUiQueryVariables
  >(GET_WORKERS_NAME, {
    variables: {
      ids: initialValue.current,
      filter: {
        shouldIncludeArchivedWorkers: true,
        ...(projectId && { projectId }),
      },
    },
    skip: !initialValue.current,
  })

  const { data, fetchMore } = useQuery<
    WorkersNameUiQuery,
    WorkersNameUiQueryVariables
  >(GET_WORKERS_NAME, {
    variables: {
      filter: {
        shouldIncludeArchivedWorkers: false,
        ...(projectId && { projectId }),
      },
      searchString: debouncedSearchString,
      pagination: {
        limit: INITIAL_LIMIT,
      },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (!debouncedSearchString) {
        setTotalCount(data.workersAggregation?.totalCount)
      }
    },
  })

  const workers = useMemo(() => {
    if (debouncedSearchString?.length) {
      return data?.workers
    }

    return uniqBy(
      [
        ...(initialWorkersData?.workers ?? []),
        ...addedWorkers,
        ...(data?.workers ?? []),
      ],
      (w) => w.id
    )
  }, [
    debouncedSearchString,
    initialWorkersData?.workers,
    data?.workers,
    addedWorkers,
  ])

  const openWorkerModal = () => setWorkerModalVisible(true)
  const closeWorkerModal = () => setWorkerModalVisible(false)

  const handleScroll = useCallback(
    async (e: BaseSyntheticEvent) => {
      const bottom =
        e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight

      const moreToLoad = totalCount > workers?.length

      if (bottom && moreToLoad && !currentlyLoadingMore) {
        e.preventDefault()

        const offsetJump =
          offset.current === 0 ? INITIAL_LIMIT : SUBSEQUENT_LIMIT
        offset.current += offsetJump

        setCurrentlyLoadingMore(true)

        await fetchMore({
          variables: {
            pagination: {
              offset: offset.current,
              limit: SUBSEQUENT_LIMIT,
            },
          },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev
            return Object.assign({}, prev, {
              workers: [
                ...(prev?.workers ?? []),
                ...(fetchMoreResult?.workers ?? []),
              ],
              workersAggregation: fetchMoreResult.workersAggregation,
            })
          },
        })

        setCurrentlyLoadingMore(false)
      }
    },
    [totalCount, workers?.length, currentlyLoadingMore, fetchMore]
  )

  const onNewWorkerAdded = async (
    worker: CreateWorkerWithUserUiMutation['createWorkerWithUser']
  ) => {
    closeWorkerModal()

    if (multiple) {
      const newWorkerIds = [...(value ?? []), worker.id]
      onChange?.(newWorkerIds)
    } else {
      onChange?.(worker.id)
    }

    const result = await fetchMore({ variables: { ids: [worker.id] } })

    if (result?.data?.workers?.length > 0) {
      setAddedWorkers((addedWorkers) => [
        ...addedWorkers,
        ...result.data.workers,
      ])
    }
  }

  const workerOptions = useMemo(() => {
    return workers?.map((w) => {
      const [subcontractor] = w.subcontractors ?? []

      return {
        value: w.id,
        label: (
          <Flexbox flexDirection="column">
            {getWorkerName(w)}
            <Text variant="caption01" secondary="_500">
              {w.trade}
              {subcontractor &&
                !hideSubbieOrigin &&
                ` from ${subcontractor?.company?.name}`}
            </Text>
          </Flexbox>
        ),
      }
    })
  }, [workers, hideSubbieOrigin])

  useEffect(() => {
    offset.current = 0
  }, [debouncedSearchString])

  return (
    <>
      <Select
        allowClear
        showSearch
        mode={multiple ? 'multiple' : undefined}
        value={value}
        onPopupScroll={handleScroll}
        onChange={onChange}
        onSearch={setSearchString}
        placeholder="Select a worker or type to search..."
        filterOption={() => true} // return everything, the API is doing the filtering
        options={workerOptions}
        dropdownRender={(menu) => (
          <WorkerDropDown menu={menu} openWorkerModal={openWorkerModal} />
        )}
        onSelect={() => setSearchString('')} // clear the search string when a worker is selected
      />

      <NewWorkerModal
        isVisible={workerModalVisible}
        handleCancel={closeWorkerModal}
        handleSuccess={onNewWorkerAdded}
      />
    </>
  )
}
